import { useCallback, useEffect, useRef } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TCallable = (...args: any[]) => any;

export function useDebounced<T extends TCallable>(callback: T, delay = 500) {
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  // provide a stable callback
  const debounced = useCallback(
    (...params: Parameters<T>) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      timeout.current = setTimeout(() => callbackRef.current(...params), delay);
    },
    [delay]
  );

  // provide a stable cancel
  const cancel = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = undefined;
    }
  }, []);

  // clear the timeout if when delay changes
  useEffect(() => {
    cancel();
    // clear any timeout on unmount
    return cancel;
  }, [delay, cancel]);

  return { debounced, cancel };
}
